import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DataState,
  EmptyState,
  PageHeader,
  SearchInput,
  Select,
  formatLapTime,
} from "@vms/ui-web";
import type { LeaderboardEntry } from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";
import { useEventLeaderboard } from "../../api/leaderboards";

interface EventOption {
  id: string;
  name: string;
}

const positionStyles: Record<number, string> = {
  1: "text-yellow-600 font-bold",
  2: "text-gray-400 font-bold",
  3: "text-amber-700 font-bold",
};

const medal: Record<number, string> = { 1: "🏆", 2: "🥈", 3: "🥉" };

/**
 * Venue-scoped Results — the cloud mirror of hub-ui's team-facing Results page
 * (`apps/hub-ui/src/pages/Results.tsx`): per-event finishing order with best lap,
 * sector splits, lap count, and podium medals. Event chosen via the dropdown
 * (deep-linkable with `?eventId=`); driver search filters the list. Backed by
 * the authed per-event hotlap leaderboard (`/api/leaderboard/by-event/:id`),
 * which auto-refreshes over the venue WS room via `useEventLeaderboard`.
 */
export function VenueResults() {
  const { currentVenue } = useOrgContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get("eventId") ?? "";
  const [driverSearch, setDriverSearch] = useState("");

  function setEventId(id: string) {
    setSearchParams(id ? { eventId: id } : {}, { replace: true });
  }

  const eventsQuery = useQuery<EventOption[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/events` : null,
  );
  const { data: leaderboard, loading, error } = useEventLeaderboard(
    currentVenue?.id,
    eventId || undefined,
  );

  const events = eventsQuery.data ?? [];
  const entries = leaderboard ?? [];

  const filtered = useMemo(() => {
    const q = driverSearch.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e: LeaderboardEntry) =>
      `${e.driver.firstName} ${e.driver.lastName}`.toLowerCase().includes(q),
    );
  }, [entries, driverSearch]);

  if (!currentVenue) return null;

  return (
    <div>
      <PageHeader title="Results" subtitle="How drivers finished, per event." />

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="max-w-sm flex-1 min-w-[16rem]">
          <Select
            label="Event"
            placeholder="Select an event"
            options={events.map((e) => ({ value: e.id, label: e.name }))}
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
          />
        </div>
        {eventId && (
          <div className="max-w-sm flex-1 min-w-[16rem]">
            <SearchInput onSearch={setDriverSearch} placeholder="Search driver…" />
          </div>
        )}
      </div>

      {!eventId ? (
        <EmptyState message="Select an event to view its results." />
      ) : (
        <DataState
          loading={loading}
          error={error}
          empty={entries.length === 0}
          emptyMessage="No lap times recorded for this event yet."
        >
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">Pos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Best Lap</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S1</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S2</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S3</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Laps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                      No drivers match “{driverSearch}”.
                    </td>
                  </tr>
                )}
                {filtered.map((entry) => (
                  <tr
                    key={`${entry.driver.id}:${entry.car}`}
                    className={entry.position <= 3 ? "bg-gray-50/50" : ""}
                  >
                    <td className={`px-4 py-3 text-sm ${positionStyles[entry.position] ?? "text-gray-700"}`}>
                      {medal[entry.position] ? (
                        <span className="text-lg" aria-label={`Position ${entry.position}`}>
                          {medal[entry.position]}
                        </span>
                      ) : (
                        entry.position
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {entry.driver.firstName} {entry.driver.lastName}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-gray-900">
                      {formatLapTime(entry.bestLapTimeMs)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">
                      {entry.bestLapSector1Ms ? formatLapTime(entry.bestLapSector1Ms) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">
                      {entry.bestLapSector2Ms ? formatLapTime(entry.bestLapSector2Ms) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">
                      {entry.bestLapSector3Ms ? formatLapTime(entry.bestLapSector3Ms) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{entry.totalLaps}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataState>
      )}
    </div>
  );
}
